/**
 * Apertura de adjuntos que el navegador no sabe mostrar.
 *
 * Las fotos de iPhone llegan como HEIC, que solo Safari renderiza: Chrome,
 * Edge y Firefox las descargan. La conversión a JPEG se hace acá, en el
 * navegador de quien abre el adjunto, y no en el backend al subirlas:
 * decodificar HEVC toma segundos y cientos de MB por foto, y en el servidor
 * bloqueaba el event loop. Así también se ven los HEIC ya guardados.
 */
import { renderLoadingPage, setLoadingMessage } from './attachment-loading-page';
import { renderImageViewerPage } from './attachment-image-viewer-page';

const OPENING_MESSAGE = {
  title: 'Abriendo adjunto',
  detail: 'Estamos preparando el documento, solo toma un momento.'
};
const CONVERTING_MESSAGE = {
  title: 'Preparando la foto',
  detail: 'Esta imagen viene en formato HEIC. La estamos convirtiendo para que puedas verla; puede tardar unos segundos.'
};

const HEIC_CONTENT_TYPES = ['image/heic', 'image/heif'];
// Tipos que se guardan cuando el backend no reconoce el archivo.
const AMBIGUOUS_CONTENT_TYPES = ['', 'application/octet-stream', 'binary/octet-stream', 'unknown'];
// Marcas ISO-BMFF (bytes 8–11, tras `ftyp`) que usan las fotos HEIC/HEIF.
const HEIC_BRANDS = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1'];
const JPEG_QUALITY = 0.9;

function getContentType(response) {
  return (response.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
}

async function hasHeicSignature(blob) {
  const header = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
  const ascii = String.fromCharCode(...header);
  return ascii.slice(4, 8) === 'ftyp' && HEIC_BRANDS.includes(ascii.slice(8, 12));
}

async function toJpeg(blob) {
  // Import dinámico: heic2any pesa ~2 MB y solo se necesita para HEIC.
  const { default: heic2any } = await import('heic2any');
  const converted = await heic2any({ blob, toType: 'image/jpeg', quality: JPEG_QUALITY });
  // Un HEIC con varias imágenes (ráfaga, live photo) devuelve un array.
  return Array.isArray(converted) ? converted[0] : converted;
}

/**
 * Descarga el adjunto a través del backend y, si es HEIC, lo convierte a JPEG.
 *
 * Se muestran estos bytes y no la URL firmada: esa URL se firma al cargar el
 * reembolso y vence a los 5 minutos, así que un adjunto abierto más tarde
 * fallaba con "Request has expired".
 *
 * @param {Window} tab - Pestaña donde se mostrará; la URL local se crea en
 *   ella para que siga válida aunque se cierre el admin.
 * @returns {Promise<{ url: string, isImage: boolean }>} `url` es una URL local
 *   al archivo (o al JPEG convertido); `isImage` indica si va al visor con rotación.
 */
async function resolveViewableUrl(tab, fetchFile, onConverting) {
  const response = await fetchFile();
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const contentType = getContentType(response);
  const blob = await response.blob();
  const mayBeHeic = HEIC_CONTENT_TYPES.includes(contentType) || AMBIGUOUS_CONTENT_TYPES.includes(contentType);
  if (mayBeHeic && (HEIC_CONTENT_TYPES.includes(contentType) || (await hasHeicSignature(blob)))) {
    onConverting();
    return { url: tab.URL.createObjectURL(await toJpeg(blob)), isImage: true };
  }
  return { url: tab.URL.createObjectURL(blob), isImage: contentType.startsWith('image/') };
}

/**
 * Abre un adjunto en una pestaña nueva, convirtiendo HEIC a JPEG para que se
 * vea en cualquier navegador. Las fotos se muestran en un visor que permite
 * rotarlas. Si algo falla, abre la URL original: el mismo comportamiento que
 * antes.
 *
 * Los bytes se piden a través del backend (`fetchFile`), no a la URL firmada:
 * los buckets de S3 no permiten CORS, así que el navegador bloquea ese fetch.
 *
 * Debe llamarse directo desde el click: la pestaña se abre antes de cualquier
 * await para que el bloqueador de popups no la frene.
 *
 * @param {string} url - URL firmada del adjunto, para abrirlo tal cual.
 * @param {(signal: AbortSignal) => Promise<Response>} [fetchFile] - Trae los
 *   bytes del adjunto. Sin ella el adjunto se abre tal cual.
 */
export async function openAttachment(url, fetchFile) {
  if (!fetchFile) {
    console.warn('[attachment-viewer] Adjunto sin id: se abre la URL original sin convertir');
    window.open(url, '_blank', 'noopener');
    return;
  }
  const tab = window.open('', '_blank');
  if (!tab) return;
  // Sin `noopener` en window.open (se necesita la referencia), se corta a mano.
  tab.opener = null;
  renderLoadingPage(tab, OPENING_MESSAGE);

  try {
    const viewable = await resolveViewableUrl(tab, fetchFile, () =>
      setLoadingMessage(tab, CONVERTING_MESSAGE)
    );
    if (viewable.isImage) {
      renderImageViewerPage(tab, viewable.url);
    } else {
      tab.location.href = viewable.url;
    }
  } catch (error) {
    console.warn('[attachment-viewer] No se pudo preparar el adjunto; se abre la URL original', error);
    tab.location.href = url;
  }
}
